<template>
  <CopyTrackWrapper
    type="strategy"
    :title="copyTrackerParams.title"
    :uuid="copyTrackerParams.uuid"
    :demo="copyTrackerParams.demo">
    <LibraryRedesignBanner v-if="libraryBannerProps" :title="libraryBannerProps.name || ''">
      <template v-slot:breadcrumbList>
        <li v-if="libraryBannerProps.uuid"><a :href="libraryFolder(libraryBannerProps.uuid)"><i class="fa fa-folder header-folder" /> {{ libraryBannerProps.name }}</a></li>
        <li v-if="libraryBannerProps.title" class="active">Strategy: {{ libraryBannerProps.title }}</li>
      </template>
    </LibraryRedesignBanner>

    <NoAccessError v-if="hasAccess === false"/>
    <div v-else-if="hasAccess" class="container">
      <CopyTrackWrapper
        :title="copyTrackerParams.title"
        :uuid="copyTrackerParams.uuid"
        type="strategy_banner">
        <StrategyBanner v-if="bannerProps" v-bind="bannerProps"/>
      </CopyTrackWrapper>

      <div class="row">
        <div class="col-sm-8">
          <CopyTrackWrapper
            :title="copyTrackerParams.title"
            :uuid="copyTrackerParams.uuid"
            type="strategy_notes">
            <StrategyNotes
              :key="discardsKey"
              v-if="notesProps"
              v-bind="notesProps"
              @notesEdited="handleNotesEdit"/>
          </CopyTrackWrapper>

          <StrategyResourceList v-if="resourcesProps" v-bind="resourcesProps"/>
          <StrategyPanels v-if="implementationProps" v-bind="implementationProps"/>
          <StrategyPanels v-if="examplesProps" v-bind="examplesProps"/>
          <ResearchCitationPanels v-if="researchCitationProps" :research-citations="researchCitationProps" />
        </div>

        <div class="col-sm-4">
          <LibraryContentPanel
            v-if="savedStrategyPanelProps"
            :content-type="CONTENT_TYPES.SAVED_STRATEGY"
            :source-content-uuid="strategyData && strategyData.uuid"
            @save="saveEdits"
            @discard="discardEdits"
            :content-save-status="contentSaveStatus"
            :content-last-save="savedStrategyPanelProps.lastUpdatedAt | formatTimeAgo"
          />
          <ShareStrategyPanel v-if="sharedStrategyPanelProps" v-bind="sharedStrategyPanelProps"/>
        </div>
      </div>

      <StrategyRelated v-if="relatedProps" v-bind="relatedProps"/>

      <SubmitFeedback v-if="feedbackProps" v-bind="feedbackProps" />
    </div>
  </CopyTrackWrapper>
</template>

<script>
import { getSavedStrategy, putSavedStrategy } from '@/api/library.js'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { setTitle } from '@/util/title'

import { strategyPageMixin } from '@/components/strategies/strategy_mixins.js'
import { authorizationCheckMixin } from '@/router/authorization_mixin'

import LibraryBanner from '@/components/library/LibraryBanner'
import LibraryRedesignBanner from '@/components/library/LibraryRedesignBanner'
import StrategyNotes from '@/components/strategies/details/StrategyNotes'
import ShareStrategyPanel from '@/components/strategies/ShareStrategyPanel'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { toolkitUrls } from '@/links'
import { CONTENT_SAVE_STATES } from '@/util/contentSaveStates'
import LibraryContentPanel from '@/components/LibraryContentPanel.vue'
import NoAccessError from '@/components/NoAccessError.vue'

export default {
  name: 'SavedStrategyPage',
  mixins: [authorizationCheckMixin, strategyPageMixin],
  components: {
    NoAccessError,
    LibraryContentPanel,
    LibraryBanner,
    LibraryRedesignBanner,
    StrategyNotes,
    ShareStrategyPanel
  },
  props: {
    uuid: {
      type: String,
      default: ''
    }
  },
  computed: {
    CONTENT_TYPES() {
      return CONTENT_TYPES
    },
    ...mapGetters('user', ['userId']),
    ...mapGetters('library', ['getUserRoleForFolder']),
    CONTENT_SAVE_STATES() {
      return CONTENT_SAVE_STATES
    },
    contentSaveStatus() {
      if (this.savedStrategyPanelProps.justSaved) {
        return CONTENT_SAVE_STATES.JUST_SAVED
      }
      if (this.savedStrategyPanelProps.saving) {
        return CONTENT_SAVE_STATES.SAVING
      }
      if (this.savedStrategyPanelProps.unsavedChanges) {
        return CONTENT_SAVE_STATES.UNSAVED
      }
      return this.CONTENT_SAVE_STATES.SAVED
    }
  },
  data() {
    return {
      copyTrackerParams: {},
      libraryBannerProps: null,
      notesProps: null,
      savedStrategyPanelProps: null,
      sharedStrategyPanelProps: null,
      feedbackProps: null,

      savedStrategyData: {},
      strategyData: {},
      editedNotes: '',
      discardsKey: 0,
      hasAccess: undefined,
      ...toolkitUrls
    }
  },
  methods: {
    ...mapActions('library', ['fetchCurrentContentFolder']),
    ...mapMutations('library', ['clearCurrentContentFolder']),
    async fetchContent() {
      try {
        let savedStrategyData = await getSavedStrategy(this.uuid)
        this.hasAccess = true
        this.parseStrategy(savedStrategyData)
      } catch (error) {
        if (error.status_code === 403) {
          this.hasAccess = false
          setTitle('UDL Strategies')
        }
        console.error('error fetching saved strategy', error)
      }
    },
    parseStrategy(savedStrategyData) {
      this.savedStrategyData = savedStrategyData

      let strategyData = savedStrategyData.source_strategy
      this.strategyData = strategyData

      setTitle(`${strategyData.title} | UDL Strategies`)

      this.setCommonProps(Object.assign({}, strategyData, savedStrategyData), false)

      this.copyTrackerParams = {
        uuid: strategyData.uuid,
        title: strategyData.title,
        demo: strategyData.demo
      }

      this.libraryBannerProps = {
        uuid: savedStrategyData.student_uuid,
        name: savedStrategyData.student_name,
        title: strategyData.title
      }

      this.notesProps = {
        notes: savedStrategyData.notes,
        studentName: savedStrategyData.student_name,
        userName: savedStrategyData.student_name,
        collapsible: true,
        editable: true
      }
      this.discardsKey++

      this.savedStrategyPanelProps = {
        studentUuid: savedStrategyData.student_uuid,
        studentName: savedStrategyData.student_name,
        savedStrategyUuid: savedStrategyData.uuid,
        sourceStrategyUuid: strategyData.uuid,
        lastUpdatedAt: savedStrategyData.updated_at,
        saving: false,
        justSaved: false,
        unsavedChanges: false
      }

      this.sharedStrategyPanelProps = {
        uuid: savedStrategyData.uuid,
        shareState: savedStrategyData.shared,
        shareLinkState: savedStrategyData.share_link || ''
      }

      this.feedbackProps = {
        type: 'Strategy Detail Page',
        uuid: strategyData.uuid,
        title: strategyData.title
      }
    },
    handleNotesEdit(editedNotes) {
      this.editedNotes = editedNotes
      this.savedStrategyPanelProps.unsavedChanges = editedNotes !== this.savedStrategyData.notes
    },
    saveEdits() {
      let payload = Object.assign(
        { uuid: this.savedStrategyData.uuid },
        { notes: this.editedNotes }
      )

      putSavedStrategy(payload).then(data => {
        this.parseStrategy(data)
        this.savedStrategyPanelProps.justSaved = true
        setTimeout(() => {
          this.savedStrategyPanelProps.justSaved = false
        }, 5000)
      }).catch(err => {
        console.error('error saving strategy', err)
      })
      this.$sendEvent('Clicked Save Changes', {
        type: CONTENT_TYPES.SAVED_STRATEGY
      })
    },
    discardEdits() {
      this.discardsKey++
      this.savedStrategyPanelProps.unsavedChanges = false
    }
  },
  eventContext() {
    return {
      saved_strategy_uuid: this.savedStrategyData.uuid,
      student_uuid: this.savedStrategyData.student_uuid,
      strategy_uuid: this.strategyData.uuid,
      strategy_title: this.strategyData.title,
      research_citations_count: this.savedStrategyData.research_citations
        ? this.savedStrategyData.research_citations.length
        : 0,
      is_library: true
    }
  },
  watch: {
    $route(to, from) {
      this.fetchContent()
    }
  },
  async created() {
    await this.fetchContent()
    await this.fetchCurrentContentFolder({
      contentUuid: this.uuid,
      userUuid: this.userId,
      contentType: CONTENT_TYPES.SAVED_STRATEGY,
    })
    if (this.hasAccess) {
      const userFolderRole = this.getUserRoleForFolder(this.savedStrategyData.student_uuid)
      this.$sendEvent(
        'Viewed Saved Strategy',
        {
          user_folder_role: userFolderRole
        },
        {
          cu: this.strategyData.uuid,
          ct: this.strategyData.title
        }
      )
    }
  },
  beforeDestroy() {
    this.clearCurrentContentFolder()
  }
}
</script>

<style>
</style>
